import React, { useRef, useEffect, useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, ShoppingCart, X } from 'lucide-react';
import { usePlayer } from '../contexts/PlayerContext';
import { useMusicCart } from '../contexts/MusicCartContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MiniPlayer = () => {
  const {
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    volume,
    togglePlay,
    nextTrack,
    prevTrack,
    updateProgress,
    updateVolume
  } = usePlayer();

  const { addTrack, isInCart } = useMusicCart();

  const [localTime, setLocalTime] = useState(0);
  const [localDuration, setLocalDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showPurchasePrompt, setShowPurchasePrompt] = useState(false);
  const [previewEnded, setPreviewEnded] = useState(false);
  const [listenerSessionId, setListenerSessionId] = useState(null);
  const [playEventSent, setPlayEventSent] = useState(false);
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const userInteractedRef = useRef(false);

  // Generate or retrieve listener session ID
  useEffect(() => {
    let sessionId = sessionStorage.getItem('listener_session_id');
    if (!sessionId) {
      sessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('listener_session_id', sessionId);
    }
    setListenerSessionId(sessionId);
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    if (!currentTrack || !currentTrack.audio_url) {
      console.log('⚠️ No track or no audio URL');
      return;
    }

    const audio = audioRef.current;
    if (!audio) {
      console.log('⚠️ Audio element not found');
      return;
    }

    console.log('🎵 Loading track:', currentTrack.title);
    console.log('🎵 Audio URL:', currentTrack.audio_url);
    console.log('🎵 Preview duration:', currentTrack.preview_duration);
    console.log('🎵 Is purchased:', currentTrack.is_purchased);
    
    // Reset states
    setPreviewEnded(false);
    setShowPurchasePrompt(false);
    setPlayEventSent(false);
    
    // Load the new track
    audio.src = currentTrack.audio_url;
    audio.volume = isMuted ? 0 : volume;
    audio.load();
    
    // Reset time
    setLocalTime(0);
    setLocalDuration(currentTrack.duration || 180);

    // Event handlers
    const handleLoadStart = () => console.log('📥 Audio loading started...');
    const handleCanPlay = () => {
      console.log('✅ Audio can play');
      // Only autoplay if user has already interacted AND isPlaying is true
      if (isPlaying && userInteractedRef.current) {
        console.log('▶️ Auto-resuming playback after track load');
        audio.play().catch(err => {
          console.error('❌ Auto-resume failed:', err);
        });
      }
    };
    const handleError = (e) => {
      console.error('❌ Audio error:', e);
      console.error('❌ Audio error details:', audio.error);
    };

    // Update time as audio plays - enforce preview limit
    const handleTimeUpdate = () => {
      const currentTime = audio.currentTime;
      setLocalTime(currentTime);
      updateProgress(currentTime, audio.duration || currentTrack.duration);

      // Check if preview limit reached (only if not purchased)
      const isPurchased = currentTrack.is_purchased;
      const previewDuration = currentTrack.preview_duration || 30;
      const previewEnabled = currentTrack.preview_enabled !== false;

      if (!isPurchased && previewEnabled && currentTime >= previewDuration && !previewEnded) {
        console.log('⏸️ Preview ended at', previewDuration, 'seconds');
        setPreviewEnded(true);
        setShowPurchasePrompt(true);
        audio.pause();
        togglePlay(); // Update UI state
        
        // Track stream completion
        if (listenerSessionId) {
          axios.post(`${API}/api/music/tracks/${currentTrack.id}/complete`, {
            listener_id: listenerSessionId
          }).catch(err => console.error('Failed to track completion:', err));
        }
      }
    };

    const handleLoadedMetadata = () => {
      console.log('📊 Metadata loaded, duration:', audio.duration);
      setLocalDuration(audio.duration || currentTrack.duration);
    };

    const handleEnded = () => {
      console.log('⏭️ Track ended, playing next');
      
      // Track stream completion
      if (listenerSessionId) {
        axios.post(`${API}/api/music/tracks/${currentTrack.id}/complete`, {
          listener_id: listenerSessionId
        }).catch(err => console.error('Failed to track completion:', err));
      }
      
      nextTrack();
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack, nextTrack, updateProgress, volume, isMuted, isPlaying, previewEnded, listenerSessionId, togglePlay]);

  if (!currentTrack) return null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * localDuration;
    setLocalTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    updateProgress(newTime, localDuration);
  };

  // Handle play/pause with direct user interaction (mobile-safe)
  const handleTogglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    // Mark that user has interacted (unlocks autoplay on mobile)
    userInteractedRef.current = true;

    // If preview ended, don't allow replay - show purchase prompt
    if (previewEnded && !currentTrack.is_purchased) {
      setShowPurchasePrompt(true);
      return;
    }

    if (isPlaying) {
      console.log('⏸️ Pausing from user click');
      audio.pause();
      togglePlay();
    } else {
      console.log('▶️ Playing from user click');
      // Track play event (only once per track load)
      if (!playEventSent && listenerSessionId) {
        axios.post(`${API}/api/music/tracks/${currentTrack.id}/play`, {
          listener_id: listenerSessionId
        }).catch(err => console.error('Failed to track play:', err));
        setPlayEventSent(true);
      }

      // Call togglePlay FIRST to update state synchronously
      togglePlay();
      // Then play the audio
      audio.play().then(() => {
        console.log('✅ Play successful');
      }).catch(err => {
        console.error('❌ Play failed:', err);
        // If play fails, revert the state
        togglePlay();
      });
    }
  };

  const handleAddToCartFromPrompt = () => {
    addTrack(currentTrack);
    setShowPurchasePrompt(false);
  };

  const handleClosePrompt = () => {
    setShowPurchasePrompt(false);
    nextTrack(); // Skip to next track
  };

  const progress = localDuration > 0 ? (localTime / localDuration) * 100 : 0;

  // Calculate display duration (show preview limit if not purchased)
  const displayDuration = !currentTrack.is_purchased && currentTrack.preview_duration
    ? Math.min(currentTrack.preview_duration, localDuration)
    : localDuration;

  return (
    <>
      {/* Purchase Prompt Modal */}
      {showPurchasePrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4" data-testid="purchase-prompt">
          <div className="bg-[#1A1A1A] border border-[#D4AF37] max-w-md w-full p-6 relative animate-fade-in">
            <button
              onClick={handleClosePrompt}
              className="absolute top-4 right-4 text-[#A3A3A3] hover:text-white transition-colors"
              data-testid="close-prompt"
            >
              <X size={20} />
            </button>

            <div className="text-center">
              {currentTrack.cover_art_url && (
                <img
                  src={currentTrack.cover_art_url}
                  alt={currentTrack.title}
                  className="w-32 h-32 mx-auto mb-4 object-cover"
                />
              )}
              <h3 className="text-xl font-['Bebas_Neue'] mb-2">Preview Complete</h3>
              <p className="text-[#A3A3A3] mb-4">
                Purchase "<span className="text-white">{currentTrack.title}</span>" to listen to the full song
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleAddToCartFromPrompt}
                  className="w-full py-3 bg-[#D4AF37] text-black font-medium hover:bg-[#E5C048] transition-colors flex items-center justify-center gap-2"
                  data-testid="add-to-cart-prompt"
                  disabled={isInCart(currentTrack.id)}
                >
                  <ShoppingCart size={18} />
                  {isInCart(currentTrack.id) ? 'Already in Cart' : `Add to Cart ($${currentTrack.price?.toFixed(2) || '0.99'})`}
                </button>
                <button
                  onClick={handleClosePrompt}
                  className="w-full py-3 border border-white/20 text-white hover:border-white/40 transition-colors"
                  data-testid="skip-track-prompt"
                >
                  Skip to Next Track
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-50 glass mini-player-bg" data-testid="mini-player">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        preload="metadata"
        crossOrigin="anonymous"
      />
      
      {/* Progress Bar */}
      <div
        ref={progressRef}
        className="h-1 bg-[#262626] cursor-pointer group"
        onClick={handleProgressClick}
      >
        <div
          className="h-full bg-[#D4AF37] transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Track Info */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
              {currentTrack.cover_art_url ? (
                <img
                  src={currentTrack.cover_art_url}
                  alt={currentTrack.title}
                  className={`w-full h-full object-cover ${isPlaying ? 'animate-spin-slow' : ''}`}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] flex items-center justify-center">
                  <div className="visualizer-bar" />
                  <div className="visualizer-bar" />
                  <div className="visualizer-bar" />
                  <div className="visualizer-bar" />
                  <div className="visualizer-bar" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" data-testid="player-track-title">{currentTrack.title}</p>
              <p className="text-xs text-[#A3A3A3] truncate">IAMHETHATIS</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={prevTrack}
              className="p-2 text-[#A3A3A3] hover:text-white transition-colors"
              data-testid="player-prev"
            >
              <SkipBack size={20} />
            </button>
            <button
              onClick={handleTogglePlay}
              className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center text-black hover:bg-[#E5C048] transition-colors"
              data-testid="player-play-pause"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
            </button>
            <button
              onClick={nextTrack}
              className="p-2 text-[#A3A3A3] hover:text-white transition-colors"
              data-testid="player-next"
            >
              <SkipForward size={20} />
            </button>
          </div>

          {/* Time & Volume */}
          <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
            <span className="text-xs text-[#A3A3A3] tabular-nums">
              {formatTime(localTime)} / {formatTime(displayDuration)}
              {!currentTrack.is_purchased && currentTrack.preview_duration && (
                <span className="ml-1 text-[#D4AF37]">(Preview)</span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="p-1 text-[#A3A3A3] hover:text-white transition-colors"
                data-testid="player-mute"
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={(e) => updateVolume(parseFloat(e.target.value))}
                className="w-20 track-progress"
                data-testid="player-volume"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default MiniPlayer;
